import { useMemo } from 'react'

export const getHorizontalPadding = (el: HTMLElement) => {
  const computedStyle = getComputedStyle(el)

  const padding =
    parseFloat(computedStyle.paddingLeft) +
    parseFloat(computedStyle.paddingRight)

  return padding
}

export const getHorizontalScrollPadding = (el: HTMLElement) => {
  const computedStyle = getComputedStyle(el)

  const paddingLeft =
    computedStyle.scrollPaddingLeft === 'auto'
      ? 0
      : parseFloat(computedStyle.scrollPaddingLeft)
  const paddingRight =
    computedStyle.scrollPaddingRight === 'auto'
      ? 0
      : parseFloat(computedStyle.scrollPaddingRight)

  const padding = paddingLeft + paddingRight

  return padding
}

export const getInnerWidth = (el: HTMLElement) => {
  const padding = getHorizontalPadding(el)

  // we don't want to include overflow tiles into the page calculations
  return el.clientWidth - padding
}

export const getScrollWidth = (el: HTMLElement) => {
  const padding = getHorizontalPadding(el)

  return el.scrollWidth - padding
}

function firstChildIndexWithOffsetLeft(
  container: HTMLElement,
  f: (offsetLeft: number) => boolean,
): number {
  return Array.from(container.children).findIndex((child) =>
    f((child as HTMLElement).offsetLeft),
  )
}

export function firstVisibleIndex(container: HTMLElement) {
  const scrollPadding = getHorizontalScrollPadding(container)
  return firstChildIndexWithOffsetLeft(
    container,
    (offsetLeft) => offsetLeft >= container.scrollLeft + scrollPadding / 2,
  )
}

function forwardNextIndex(
  container: HTMLElement,
  scrollCount: number,
  tileCount: number,
): number {
  if (scrollCount === 0) {
    const containerWidth = getInnerWidth(container)
    const scrollWidth = getScrollWidth(container)

    const scrollPadding = getHorizontalScrollPadding(container)

    const tileWidth = scrollWidth / tileCount
    const tilesPerPage = Math.floor(containerWidth / tileWidth) || 1

    return firstChildIndexWithOffsetLeft(
      container,
      (offsetLeft) =>
        offsetLeft >=
        container.scrollLeft + scrollPadding / 2 + tileWidth * tilesPerPage,
    )
  } else {
    const idx = firstVisibleIndex(container)

    return Math.min(idx + scrollCount, container.children.length - 1)
  }
}

function backwardNextIndex(
  container: HTMLElement,
  scrollCount: number,
  tileCount: number,
): number {
  if (scrollCount === 0) {
    const containerWidth = getInnerWidth(container)
    const scrollWidth = getScrollWidth(container)

    const scrollPadding = getHorizontalScrollPadding(container)

    const tileWidth = scrollWidth / tileCount
    const tilesPerPage = Math.floor(containerWidth / tileWidth) || 1

    return firstChildIndexWithOffsetLeft(
      container,
      (offsetLeft) =>
        offsetLeft >=
        container.scrollLeft + scrollPadding / 2 - tileWidth * tilesPerPage,
    )
  } else {
    const idx = firstVisibleIndex(container)

    return Math.max(idx - scrollCount, 0)
  }
}

function goToIndex(
  container: HTMLElement,
  idx: number,
  behavior: 'smooth' | 'instant' = 'smooth',
) {
  const computedContainerStyle = getComputedStyle(container)

  const el = container.children.item(idx) as HTMLElement
  if (!el) return

  container.scrollTo({
    left: el.offsetLeft - parseFloat(computedContainerStyle.paddingLeft),
    behavior,
  })
}

export function goForward(
  container: HTMLElement,
  scrollCount: number,
  tileCount: number,
) {
  goToIndex(container, forwardNextIndex(container, scrollCount, tileCount))
}

export function goBackward(
  container: HTMLElement,
  scrollCount: number,
  tileCount: number,
) {
  goToIndex(container, backwardNextIndex(container, scrollCount, tileCount))
}

export function forwardLoop(
  container: HTMLElement,
  scrollCount: number,
  tileCount: number,
) {
  const currentFirstIndex = firstVisibleIndex(container)
  const uniqueTileCount = container.children.length / 2

  if (currentFirstIndex >= uniqueTileCount) {
    goToIndex(container, currentFirstIndex - uniqueTileCount, 'instant')
  }

  goForward(container, scrollCount, tileCount)
}

export function backwardLoop(
  container: HTMLElement,
  scrollCount: number,
  tileCount: number,
) {
  const currentFirstIndex = firstVisibleIndex(container)
  const uniqueTileCount = container.children.length / 2

  if (currentFirstIndex <= 0) {
    goToIndex(container, currentFirstIndex + uniqueTileCount, 'instant')
  }

  goBackward(container, scrollCount, tileCount)
}

interface Scroller {
  forward: () => void
  backward: () => void
  firstVisibleIndex: () => number
  tileCount: number
}

export function buildScroller(
  loops: boolean,
  container: HTMLElement | null,
  scrollCount: number,
): Scroller {
  const tileCount = container ? container.children.length : 0
  if (loops) {
    const uniqueTileCount = tileCount ? tileCount / 2 : 0
    return {
      forward: () =>
        container && forwardLoop(container, scrollCount, uniqueTileCount),
      backward: () =>
        container && backwardLoop(container, scrollCount, uniqueTileCount),
      firstVisibleIndex: () => {
        if (!container) {
          return 0
        }

        const idx = firstVisibleIndex(container)
        return idx > uniqueTileCount ? idx - uniqueTileCount : idx
      },
      tileCount: uniqueTileCount,
    }
  } else {
    return {
      forward: () => container && goForward(container, scrollCount, tileCount),
      backward: () =>
        container && goBackward(container, scrollCount, tileCount),
      firstVisibleIndex: () => {
        if (!container) {
          return 0
        }

        return firstVisibleIndex(container)
      },
      tileCount: container?.children.length ?? 0,
    }
  }
}

export const useScroller = (
  loops: boolean,
  container: HTMLElement | null,
  scrollCount: number,
) =>
  useMemo(
    () => buildScroller(loops, container, scrollCount),
    // Include children.length to ensure that additions/removals from set of items updates the scroller
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loops, container, scrollCount, container?.children.length],
  )
